import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Download, Loader2, Sparkles, RefreshCw } from "lucide-react";
import { http } from "@/lib/api";
import { useResult } from "@/pages/Results";
import { Meter } from "@/components/ProfileCharts";
import { DownloadModal } from "@/components/DownloadModal";
import { LearningChecklist } from "@/components/LearningChecklist";
import { useLang } from "@/i18n";

const Block = ({ n, title, children, testId }) => (
  <section className="py-10 border-b border-[#1C242B]/10" data-testid={testId}>
    <p className="font-mono-ui text-xs text-[#D96B43]">{String(n).padStart(2, "0")}</p>
    <h2 className="text-2xl sm:text-3xl font-bold tracking-tight mt-1 mb-6">{title}</h2>
    {children}
  </section>
);

const AiText = ({ text }) => (
  <div className="space-y-2 text-[15px] leading-relaxed text-slate-700" data-testid="ai-analysis-text">
    {text.split("\n").map((l, i) => {
      const s = l.trim();
      if (!s) return <div key={i} className="h-2" />;
      if (s.startsWith("#")) return <h3 key={i} className="font-display text-xl font-semibold text-[#1C242B] pt-4">{s.replace(/^#+\s*/, "")}</h3>;
      if (/^[-*] /.test(s)) return <p key={i} className="pl-4 border-l-2 border-[#D96B43]/40">{s.slice(2).replace(/\*\*/g, "")}</p>;
      return <p key={i}>{s.replace(/\*\*/g, "")}</p>;
    })}
  </div>
);

const AiSection = ({ token, paid, initialStatus, initialText, onUnlock }) => {
  const [status, setStatus] = useState(initialStatus);
  const [text, setText] = useState(initialText);

  useEffect(() => {
    if (!paid) return;
    if (status === "none") http.post(`/results/${token}/ai-analysis`).then((r) => setStatus(r.data.ai_status));
    if (status !== "generating") return;
    const t = setInterval(async () => {
      const { data } = await http.get(`/results/${token}/ai-analysis`);
      setStatus(data.ai_status);
      if (data.ai_text) setText(data.ai_text);
    }, 4000);
    return () => clearInterval(t);
  }, [status, paid, token]);

  const retry = async () => { await http.post(`/results/${token}/ai-analysis`).catch(() => {}); setStatus("generating"); };

  if (!paid) return (
    <div className="rounded-2xl bg-[#1C242B] text-[#FAF8F5] p-8" data-testid="ai-locked-panel">
      <Sparkles className="text-[#F0A584]" />
      <p className="font-display text-xl font-semibold mt-3">AI evaluation is optional</p>
      <p className="text-slate-300 mt-2 max-w-2xl text-sm">A premium AI model reads your full profile and writes a personal narrative, hidden strengths, blind spots, career fit reasoning and a 90-day action plan. A contribution from $3 covers its running cost.</p>
      <button onClick={onUnlock} data-testid="unlock-ai-btn" className="mt-5 rounded-full bg-[#D96B43] px-6 py-3 text-sm font-semibold hover:bg-[#C05630] transition-colors">Unlock AI evaluation</button>
    </div>
  );
  if (status === "ready" && text) return <AiText text={text} />;
  if (status === "failed") return <div className="text-sm" data-testid="ai-failed">The AI analysis could not be generated. <button onClick={retry} className="underline inline-flex items-center gap-1" data-testid="ai-retry-btn"><RefreshCw size={14} /> Retry</button></div>;
  return <div className="flex items-center gap-3 text-slate-600" data-testid="ai-generating"><Loader2 className="animate-spin text-[#D96B43]" /> Writing your personal analysis. This takes about a minute…</div>;
};

export default function Report() {
  const { token } = useParams();
  const { data } = useResult(token);
  const [modal, setModal] = useState(null);
  const { t } = useLang();
  if (!data) return <div className="min-h-[60vh] grid place-items-center"><Loader2 className="animate-spin text-[#D96B43]" /></div>;
  const r = data.result, p = r.profile, st = r.strengths;
  const chosen = r.careers.find((c) => c.id === data.selected_career) || r.careers[0];

  return (
    <div className="max-w-4xl mx-auto px-5 sm:px-8 pt-8 pb-10">
      <div className="flex flex-wrap justify-between items-center gap-3 no-print">
        <Link to={`/results/${token}`} data-testid="report-back-link" className="inline-flex items-center gap-2 text-sm text-slate-600 hover:text-[#1C242B]"><ArrowLeft size={16} /> Back to results</Link>
        <button data-testid="report-download-btn" onClick={() => setModal("static")} className="inline-flex items-center gap-2 rounded-full bg-[#D96B43] text-white px-5 py-2.5 text-sm font-semibold hover:bg-[#C05630] transition-colors">
          <Download size={16} /> Download PDF
        </button>
      </div>
      <header className="pt-10 pb-8 border-b border-[#1C242B]/10 fade-up">
        <p className="overline-label">Finalized report</p>
        <h1 className="mt-3 text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-none">Your Career &amp; <span className="font-serif-accent italic font-medium text-[#D96B43]">Aptitude</span> Report</h1>
        <p className="mt-5 text-slate-600">Holland code <b>{p.holland_code}</b> · Top intelligences: <b>{st.top_intelligences.map((x) => t(x.label)).join(", ")}</b> · Chosen path: <b data-testid="report-chosen-career">{chosen.title}</b></p>
      </header>

      <Block n={1} title="AI-enhanced deep analysis" testId="report-ai-section">
        <AiSection token={token} paid={data.ai_paid} initialStatus={data.ai_status} initialText={data.ai_text} onUnlock={() => setModal("ai")} />
      </Block>
      <Block n={2} title="Aptitude test performance" testId="report-aptitudes">
        <div className="grid gap-4">{Object.values(p.sections).map((s) => <Meter key={s.title} label={`${s.title} (${s.correct}/${s.total})`} value={s.pct} />)}</div>
        <p className="mt-6 text-[15px] text-slate-700">Strongest: <b>{st.top_aptitudes.map((a) => a.label).join(", ")}</b>. Growth areas: <b>{st.growth_aptitudes.map((a) => a.label).join(", ")}</b>, skills that respond well to practice.</p>
      </Block>
      <Block n={3} title="Reasoning types & intelligences" testId="report-reasoning">
        <div className="grid md:grid-cols-2 gap-8">
          <div className="grid gap-4">{Object.entries(p.reasoning).map(([k, v]) => <Meter key={k} label={k} value={v} color="#3E6B5C" />)}</div>
          <div className="grid gap-4">{Object.entries(p.intelligences).map(([k, v]) => <Meter key={k} label={k} value={v} />)}</div>
        </div>
      </Block>
      <Block n={4} title="Knowledge bases & interests" testId="report-knowledge">
        <div className="grid md:grid-cols-2 gap-8">
          <div className="grid gap-4">{Object.entries(p.dims).filter(([k]) => k.startsWith("k_")).map(([k, v]) => <Meter key={k} label={k.slice(2).replace(/^./, (c) => c.toUpperCase())} value={v} color="#2563EB" />)}</div>
          <div className="grid gap-4">{Object.entries(p.riasec).map(([k, v]) => <Meter key={k} label={r.riasec_names[k]} value={v} color="#D97706" />)}</div>
        </div>
      </Block>
      <Block n={5} title="Work style & environment needs" testId="report-workstyle">
        <div className="grid md:grid-cols-2 gap-x-8 gap-y-4">{Object.values(st.work_style).map((w) => <Meter key={w.label} label={w.label} value={w.score} color="#3E6B5C" />)}</div>
      </Block>
      <Block n={6} title="Career clusters, ranked" testId="report-clusters">
        <div className="grid sm:grid-cols-2 gap-3">
          {r.clusters.map((c) => (
            <div key={c.id} className="flex items-center gap-3 rounded-xl bg-white border border-[#1C242B]/10 p-3.5">
              <span className="w-2 h-8 rounded-full" style={{ background: c.color }} />
              <span className="flex-1 text-sm font-medium">{c.label}</span>
              <span className="font-mono-ui text-sm text-[#D96B43]">{c.score}%</span>
            </div>
          ))}
        </div>
      </Block>
      <Block n={7} title="Top 10 career alignments" testId="report-top-careers">
        <div className="space-y-5">
          {r.careers.slice(0, 10).map((c, i) => (
            <div key={c.id} className="rounded-2xl bg-white border border-[#1C242B]/10 p-5">
              <div className="flex justify-between gap-3">
                <Link to={`/results/${token}/career/${c.id}`} className="font-display text-lg font-semibold hover:text-[#D96B43]">{i + 1}. {c.title}</Link>
                <span className="font-mono-ui text-[#D96B43] font-semibold">{c.score}%</span>
              </div>
              <p className="text-xs text-slate-500 mt-1">Public: {c.public} · People: {c.people} · {c.setting} · Physical: {c.physical}</p>
              <ul className="mt-3 space-y-1.5 text-sm text-slate-700">{c.reasons.map((x) => <li key={x}>• {x}</li>)}</ul>
            </div>
          ))}
        </div>
      </Block>
      <Block n={8} title={`Your next steps: ${chosen.title}`} testId="report-next-steps">
        <p className="text-[15px] text-slate-700">The PDF includes the full role facts, realistic expectations and step-by-step plan. <Link className="underline text-[#3E6B5C]" to={`/results/${token}/career/${chosen.id}`}>Open the career page</Link> to read them here or pick a different path.</p>
      </Block>

      {data.favorites?.length > 0 && (
        <Block n={9} title="Your learning checklist" testId="report-saved-resources">
          <LearningChecklist token={token} favorites={data.favorites} />
        </Block>
      )}

      {modal && <DownloadModal open onOpenChange={(o) => !o && setModal(null)} token={token} pricing={data.pricing} aiPaid={data.ai_paid} aiReady={data.ai_status === "ready"} defaultType={modal} />}
    </div>
  );
}
